import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

const ARTICLES_COLLECTION = 'articles';

// Get all published articles (for public view)
export async function getAllArticles() {
  const articlesRef = collection(db, ARTICLES_COLLECTION);
  const q = query(
    articlesRef,
    where('published', '==', true),
    orderBy('date', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

// Get all articles including drafts (for admin view)
export async function getAllArticlesAdmin() {
  const articlesRef = collection(db, ARTICLES_COLLECTION);
  const q = query(articlesRef, orderBy('date', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
}

// Get article by slug (public - only published articles)
export async function getArticleBySlug(slug) {
  const articlesRef = collection(db, ARTICLES_COLLECTION);
  // First try to get published article (works for everyone)
  const q = query(
    articlesRef,
    where('slug', '==', slug.toLowerCase()),
    where('published', '==', true)
  );
  const snapshot = await getDocs(q);
  if (!snapshot.empty) {
    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() };
  }
  return null;
}

// Get article by slug (admin - includes drafts)
export async function getArticleBySlugAdmin(slug) {
  const articlesRef = collection(db, ARTICLES_COLLECTION);
  const q = query(articlesRef, where('slug', '==', slug.toLowerCase()));
  const snapshot = await getDocs(q);
  if (snapshot.empty) {
    return null;
  }
  const doc = snapshot.docs[0];
  return { id: doc.id, ...doc.data() };
}

// Get article by ID
export async function getArticleById(articleId) {
  const articleRef = doc(db, ARTICLES_COLLECTION, articleId);
  const snapshot = await getDoc(articleRef);
  if (!snapshot.exists()) {
    return null;
  }
  return { id: snapshot.id, ...snapshot.data() };
}

// Add new article
export async function addArticle({
  title,
  slug,
  category,
  image,
  excerpt,
  content,
  date,
  readTime,
  hasMap,
  mapConfig,
  published
}) {
  const articlesRef = collection(db, ARTICLES_COLLECTION);
  const newArticle = {
    title,
    slug: slug.toLowerCase(),
    category,
    image: image || '',
    excerpt: excerpt || '',
    content: content || '',
    date: date || new Date(),
    readTime: readTime || '5 min read',
    hasMap: hasMap || false,
    mapConfig: mapConfig || {
      title: '',
      description: '',
      architect: null,
      selectedCourseIds: []
    },
    published: published || false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };
  const docRef = await addDoc(articlesRef, newArticle);
  return { id: docRef.id, ...newArticle };
}

// Update existing article
export async function updateArticle(articleId, updates) {
  const articleRef = doc(db, ARTICLES_COLLECTION, articleId);
  const updateData = {
    ...updates,
    updatedAt: serverTimestamp()
  };
  // Ensure slug is lowercase if being updated
  if (updateData.slug) {
    updateData.slug = updateData.slug.toLowerCase();
  }
  await updateDoc(articleRef, updateData);
  return { id: articleId, ...updateData };
}

// Delete article
export async function deleteArticle(articleId) {
  const articleRef = doc(db, ARTICLES_COLLECTION, articleId);
  await deleteDoc(articleRef);
  return articleId;
}

// Generate unique slug from title
export async function generateUniqueSlug(title) {
  // Convert title to slug format
  let baseSlug = title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .trim();

  // Check if slug exists
  const existing = await getArticleBySlug(baseSlug);
  if (!existing) {
    return baseSlug;
  }

  // If exists, add a number suffix
  let counter = 2;
  let newSlug = `${baseSlug}-${counter}`;
  while (await getArticleBySlug(newSlug)) {
    counter++;
    newSlug = `${baseSlug}-${counter}`;
  }

  return newSlug;
}

// Check if slug is available (for validation)
export async function isSlugAvailable(slug, excludeArticleId = null) {
  const existing = await getArticleBySlug(slug);
  if (!existing) {
    return true;
  }
  // If we're editing and the slug belongs to the same article, it's available
  if (excludeArticleId && existing.id === excludeArticleId) {
    return true;
  }
  return false;
}
